import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useCreateAdminUserMutation } from "@/api/api";
import { getApiErrorDetail } from "@/api/errors";
import { HGAdminUserCreate } from "@/common/types";
import { Button } from "../button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../dialog";
import { Field, FieldLabel } from "../field";
import { Input } from "../input";
import { Switch } from "../switch";

interface FormValues {
  username: string;
  password: string;
  name: string;
  email: string;
  is_admin: boolean;
  records_exempt: boolean;
}

const DEFAULT_VALUES: FormValues = {
  username: "",
  password: "",
  name: "",
  email: "",
  is_admin: false,
  records_exempt: false,
};

export default function AdminUserCreateDialog({
  onError,
}: {
  onError: (message: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [createUser, { isLoading }] = useCreateAdminUserMutation();
  const form = useForm<FormValues>({ defaultValues: DEFAULT_VALUES });

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      form.reset(DEFAULT_VALUES);
      setLocalError(null);
    }
    setOpen(nextOpen);
  };

  const onSubmit = async (values: FormValues) => {
    setLocalError(null);
    onError(null);
    const body: HGAdminUserCreate = {
      username: values.username,
      password: values.password,
      is_admin: values.is_admin,
      records_exempt: values.records_exempt,
    };
    if (values.name) body.name = values.name;
    if (values.email) body.email = values.email;

    try {
      await createUser(body).unwrap();
      handleOpenChange(false);
    } catch (e) {
      setLocalError(
        getApiErrorDetail(e, "Could not create that user. Please try again."),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button>Create user</Button>
      </DialogTrigger>
      <DialogContent>
        {/* The form must live inside DialogContent, not wrap it: DialogContent
            portals its children to document.body, so a <form> wrapping it
            would have no real DOM descendants and its submit button would
            never fire an onSubmit. */}
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>Create user</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <Controller
              name="username"
              control={form.control}
              rules={{ required: true }}
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="createUsername">Username</FieldLabel>
                  <Input {...field} id="createUsername" autoComplete="off" />
                </Field>
              )}
            />
            <Controller
              name="password"
              control={form.control}
              rules={{ required: true, minLength: 10 }}
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="createPassword">Password</FieldLabel>
                  <Input
                    {...field}
                    id="createPassword"
                    type="password"
                    autoComplete="new-password"
                  />
                </Field>
              )}
            />
            <Controller
              name="name"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="createName">Name (optional)</FieldLabel>
                  <Input {...field} id="createName" />
                </Field>
              )}
            />
            <Controller
              name="email"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <FieldLabel htmlFor="createEmail">Email (optional)</FieldLabel>
                  <Input {...field} id="createEmail" type="email" />
                </Field>
              )}
            />
            <Controller
              name="is_admin"
              control={form.control}
              render={({ field }) => (
                <Field orientation="horizontal">
                  <FieldLabel htmlFor="createIsAdmin">Admin</FieldLabel>
                  <Switch
                    id="createIsAdmin"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </Field>
              )}
            />
            <Controller
              name="records_exempt"
              control={form.control}
              render={({ field }) => (
                <Field orientation="horizontal">
                  <FieldLabel htmlFor="createRecordsExempt">
                    Exempt from daily record limit
                  </FieldLabel>
                  <Switch
                    id="createRecordsExempt"
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </Field>
              )}
            />
            {localError && (
              <p role="alert" className="text-sm text-destructive">
                {localError}
              </p>
            )}
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" type="button">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={isLoading}>
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
